/**
 * Row model + client-side filtering/sorting for the pipeline Review queue
 * (SE-5499).
 * @remarks
 * Pure helpers, deliberately free of React so the Review-queue invariants can be
 * asserted directly in unit tests:
 *   - pending + resolved items coexist in ONE list (AC4), pending first;
 *   - a resolved item stays visible, flagged resolved (it does not disappear);
 *   - the scout-flags filter is applied HERE, client-side, because
 *     `pipelineReviewQueue` exposes no `source` argument (SE-5498).
 *
 * Lives in `features/player-kanban/utils` (not next to the component) because
 * component directories are restricted to index/Container/View files by the
 * project's component-structure lint rule. Mirrors `pipelineTableRows.ts`.
 * @module features/player-kanban/utils/reviewQueueRows
 */
import {
  type KanbanCardOutcome,
  type PipelineReviewRequestFragmentFragment,
  PipelineReviewSource,
  PipelineReviewStatus,
} from "@/generated/graphql";

/** One review request, shaped for the Review-queue renderer. */
export interface ReviewQueueRow {
  /** Review request id — the row identity. */
  readonly id: string;
  /** Lifecycle status (PENDING / ACCEPTED / DECLINED). */
  readonly status: PipelineReviewStatus;
  /** What produced the request (AUTOMATION_RULE / MANUAL / SCOUT_FLAG). */
  readonly source: PipelineReviewSource;
  /** Candidate player id — resolved to a name per-row via getPlayer. */
  readonly playerId: string;
  /** Pipeline board the request concerns. */
  readonly kanbanBoardId: string;
  /** Candidate card the request concerns. */
  readonly kanbanCardId: string;
  /**
   * Display name for the row's origin. The rule name when present; otherwise a
   * readable fallback derived from the source so a MANUAL / SCOUT_FLAG row is
   * never blank.
   */
  readonly ruleName: string;
  /** Stage the card is in now (null when that stage no longer exists). */
  readonly fromPhaseName: string | null;
  /** Stage the request proposes moving into (null when it no longer exists). */
  readonly targetPhaseName: string | null;
  /** Outcome proposed for a terminal target; null for a non-terminal target. */
  readonly requestedOutcome: KanbanCardOutcome | null;
  /** Rationale supplied by the producer. */
  readonly requestReason: string | null;
  /** Trimmed decision reason (present on an acceptance, optional on decline). */
  readonly decisionReason: string | null;
  /** When the request was raised (ISO string). */
  readonly createdAt: string;
  /** When the terminal decision committed; null while PENDING. */
  readonly decidedAt: string | null;
  /** True while awaiting a decision — drives the Accept/Decline actions. */
  readonly isPending: boolean;
  /** True once accepted or declined — drives the resolved badge. */
  readonly isResolved: boolean;
}

/** Client-side source filter options for the Review queue. */
export type ReviewQueueSourceFilter =
  | "all"
  | "automation"
  | "manual"
  | "scout_flag";

/** Map a source-filter option to the enum it matches (null = "all"). */
const FILTER_TO_SOURCE: Record<
  ReviewQueueSourceFilter,
  PipelineReviewSource | null
> = {
  all: null,
  automation: PipelineReviewSource.AutomationRule,
  manual: PipelineReviewSource.Manual,
  scout_flag: PipelineReviewSource.ScoutFlag,
};

/** Readable fallback origin label when a request carries no rule name. */
const SOURCE_FALLBACK_LABEL: Record<PipelineReviewSource, string> = {
  [PipelineReviewSource.AutomationRule]: "Automation rule",
  [PipelineReviewSource.Manual]: "Manual request",
  [PipelineReviewSource.ScoutFlag]: "Scout flag",
};

/**
 * Build a single Review-queue row from a request fragment.
 * @param request - The review request fragment.
 * @returns The renderer-ready row.
 */
const toRow = (
  request: PipelineReviewRequestFragmentFragment
): ReviewQueueRow => {
  const isPending = request.status === PipelineReviewStatus.Pending;
  return {
    id: request.id,
    status: request.status,
    source: request.source,
    playerId: request.playerId,
    kanbanBoardId: request.kanbanBoardId,
    kanbanCardId: request.kanbanCardId,
    ruleName:
      request.automationRuleName?.trim() ||
      SOURCE_FALLBACK_LABEL[request.source],
    fromPhaseName: request.fromPhaseName ?? null,
    targetPhaseName: request.targetPhaseName ?? null,
    requestedOutcome: request.requestedOutcome ?? null,
    requestReason: request.requestReason ?? null,
    decisionReason: request.decisionReason ?? null,
    createdAt: request.createdAt,
    decidedAt: request.decidedAt ?? null,
    isPending,
    isResolved: !isPending,
  };
};

/**
 * De-duplicate requests by id (the three per-status queries never overlap in
 * practice, but a request that flips status mid-session could otherwise appear
 * in two result sets), then map to rows.
 * @param requests - Review request fragments from one or more status queries.
 * @returns One row per unique request id.
 */
export const buildReviewQueueRows = (
  requests: readonly PipelineReviewRequestFragmentFragment[]
): ReviewQueueRow[] => {
  // Construct the Map from an entry list (later ids win) — no imperative
  // `.set()` mutation, so the functional-immutability lint stays happy.
  const byId = new Map(requests.map(request => [request.id, request]));
  return [...byId.values()].map(toRow);
};

/**
 * Filter rows by producer source, client-side (the query has no `source` arg).
 * @param rows - The rows to filter.
 * @param filter - The active source filter.
 * @returns Rows matching the filter (all rows when `filter` is "all").
 */
export const filterReviewQueueRowsBySource = (
  rows: readonly ReviewQueueRow[],
  filter: ReviewQueueSourceFilter
): ReviewQueueRow[] => {
  const source = FILTER_TO_SOURCE[filter];
  if (source === null) return [...rows];
  return rows.filter(row => row.source === source);
};

/**
 * Sort rows for display: pending (actionable) first, then resolved, each group
 * newest-first. Non-mutating.
 * @param rows - Rows to sort.
 * @returns A new, sorted array.
 */
export const sortReviewQueueRows = (
  rows: readonly ReviewQueueRow[]
): ReviewQueueRow[] =>
  [...rows].sort((a, b) => {
    if (a.isPending !== b.isPending) return a.isPending ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });

/**
 * Whole hours between a review request being raised and its decision (SE-5508).
 *
 * Lives here with the rest of the row model's pure helpers so the clamping and
 * the unparseable-timestamp fallback can be asserted directly, without
 * rendering the queue.
 *
 * Clamped at 0 so clock skew between the server's `createdAt` and the client's
 * clock can never produce a negative age, and returns 0 rather than `NaN` for
 * an unparseable timestamp — `NaN` would fail the strict Yup number schema and
 * silently drop the whole `review_item_resolved` event.
 * @param createdAt - ISO timestamp the request was raised.
 * @param nowMs - Current epoch milliseconds.
 * @returns Age in hours, rounded to one decimal place.
 */
export const resolveReviewAgeHours = (
  createdAt: string,
  nowMs: number
): number => {
  const createdMs = Date.parse(createdAt);
  if (Number.isNaN(createdMs)) return 0;
  return Math.max(0, Math.round(((nowMs - createdMs) / 3_600_000) * 10) / 10);
};
